import LogoIcon from "@/components/LogoIcon";
import { Button, buttonVariants } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useTrackPageView } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { Link } from "@inertiajs/react";
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";
import { useState } from "react";

function useSidebarOpen() {
  const [isOpen, setIsOpen] = useState(() => {
    // Initialize from localStorage, default to true if not set
    const saved = localStorage.getItem("adminSidebarOpen");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    localStorage.setItem("adminSidebarOpen", JSON.stringify(newState));
  };

  return { isOpen, toggle };
}

function NavLink({
  href,
  children,
  isActive,
}: {
  href: string;
  children: React.ReactNode;
  isActive: boolean;
}) {
  return (
    <Link
      href={href}
      className={cn(
        buttonVariants({ variant: isActive ? "secondary" : "ghost" }),
        "w-full justify-start",
      )}
    >
      {children}
    </Link>
  );
}

type Props = {
  children: React.ReactNode;
  pageName: "waitlist" | "users" | "testing" | "adjustments";
};

export default function AdminLayout({ children, pageName }: Props) {
  const { isOpen: isSidebarOpen, toggle: toggleSidebar } = useSidebarOpen();

  useTrackPageView();

  return (
    <div>
      <div className="mx-auto">
        <div className="flex flex-1 items-center sm:items-stretch sm:justify-start bg-primary">
          <div className="flex flex-shrink-0 items-center">
            <div className="overflow-hidden">
              <span className="flex space-x-2 text-sm items-center text-white font-semibold uppercase tracking-wider align-center py-2 px-4">
                <span>
                  <LogoIcon className="h-7 w-9 fill-white inline" /> Teeba admin
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-full flex space-x-4">
        <div className={cn("p-4 pr-0 w-48", isSidebarOpen ? "flex" : "hidden")}>
          <div className="space-y-2">
            <NavLink href="/admin/wait_list" isActive={pageName === "waitlist"}>
              Waitlist
            </NavLink>
            <NavLink href="/admin/users" isActive={pageName === "users"}>
              Users
            </NavLink>
            <NavLink href="/admin/testing" isActive={pageName === "testing"}>
              Testing
            </NavLink>
            <NavLink href="/admin/adjustments" isActive={pageName === "adjustments"}>
              Adjustments
            </NavLink>
          </div>
          <Separator orientation="vertical" className="self-stretch ml-4" />
        </div>

        <div className="flex-1 min-w-0 p-4 pl-0">
          <div className="flex items-center space-x-1 -mx-2">
            {!isSidebarOpen ? (
              <Button
                className="h-7 w-7"
                size="icon"
                variant="ghost"
                onClick={() => toggleSidebar()}
              >
                <PanelLeftOpenIcon />
              </Button>
            ) : (
              <Button
                className="h-7 w-7"
                size="icon"
                variant="ghost"
                onClick={() => toggleSidebar()}
              >
                <PanelLeftCloseIcon />
              </Button>
            )}
            <Separator orientation="vertical" className="h-4" />
            <span className="pl-2 font-semibold capitalize">{pageName}</span>
          </div>

          <div className="py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}
